import React, { FC, useCallback, useMemo, useState } from 'react';
import {
	addBuyerSubscribersForBuyers,
	addSupplierSubscribersForSuppliers,
} from '../../../thunks/work_orders_subscribers_thunks';
import { ROLE_TYPES } from '../../../utils/DataConstants';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { withUserType } from '../../withUserType';
import { nullSafeGet, nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import { getSubscribeObjectForWorkOrder } from '../../../utils/NotificationUtils';
import { message } from 'antd';
import { WORK_ORDERS_CRUD_ACTION_CREATORS } from '../../../actions/work_orders_actions';
import WorkOrderNotificationActionCard from './WorkOrderNotificationActionCard';
import WorkOrderNotificationSuccessCard from './WorkOrderNotificationSuccessCard';

const WorkOrderNotificationsSubscribePage: FC<any> = ({
	workOrder,
	userType,
	currentUser,
	match,
	subscribeToWO,
	history,
	updateWorkOrderInRedux,
}): React.ReactElement => {
	const [saving, setSaving] = useState(false);

	const workOrderId = match.params.id;

	const subscriberList = useMemo(() => {
		return userType === ROLE_TYPES.SUPPLIER
			? nullSafeGetOrElse('supplierSubscriberEmails', workOrder, [])
			: nullSafeGetOrElse('buyerSubscriberEmails', workOrder, []);
	}, [workOrder, userType]);

	const isSubscribed = useMemo(
		() => subscriberList.includes(nullSafeGetOrElse('email', currentUser, '')),
		[currentUser, subscriberList]
	);

	const onSubscribe = useCallback(() => {
		setSaving(true);
		const subscribeObject = getSubscribeObjectForWorkOrder(isSubscribed, workOrderId, currentUser);
		subscribeToWO(subscribeObject, workOrder)
			.then((data) => {
				updateWorkOrderInRedux(data);
				message.success('Subscribed successfully!');
			})
			.catch(message.error)
			.finally(() => setSaving(false));
	}, [currentUser, isSubscribed, subscribeToWO, updateWorkOrderInRedux, workOrder, workOrderId]);

	const onCancel = useCallback(() => {
		history.replace('/');
	}, [history]);

	const onUnsubscribe = useCallback(() => {
		history.replace(`/${userType}/workorder_notifications/${workOrderId}/unsubscribe`);
	}, [history, userType, workOrderId]);

	if (!nullSafeGet('id', workOrder)) {
		history.replace('/');
	}

	return isSubscribed ? (
		<WorkOrderNotificationSuccessCard
			workOrderId={workOrderId}
			successTitle="Subscription Restored"
			revertElement={
				<>
					Your notification settings for{' '}
					<span className="font-semibold">{`WO #${workOrderId}`}</span> have been restored. You’ll
					now receive all notifications you had before unsubscribing.
				</>
			}
			onReverseAction={onUnsubscribe}
		/>
	) : (
		<WorkOrderNotificationActionCard
			workOrderId={workOrderId}
			saving={saving}
			onConfirm={onSubscribe}
			onCancel={onCancel}
			isSubscribeAction
		/>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	workOrder: state.work_orders.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	subscribeToWO: (obj, workOrder) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? addSupplierSubscribersForSuppliers(obj)(workOrder)
				: addBuyerSubscribersForBuyers(obj)(workOrder)
		),
	updateWorkOrderInRedux: (workOrder) => {
		dispatch(WORK_ORDERS_CRUD_ACTION_CREATORS.updateSuccess(workOrder));
	},
});

export default withRouter(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(WorkOrderNotificationsSubscribePage))
);
